import axios from 'axios';
import { AuthResponse } from '../auth';
import {
  LoginCheck,
  LoginSuggestion,
  StaffCount,
  StaffCreated,
  StaffCreateInput,
  StaffJournalPage,
  StaffMember,
  StaffOverview,
  StaffSession,
  StaffTarget
} from './types';

/**
 * Сотрудники точки: права, зону видимости, ранг и лимит проверяет сервер (PROJECT_NOTES §44).
 * Кабинет точки работает со своими подчинёнными, админка - с подчинёнными точки masterId.
 * Маршруты /:id/* админка находит точку по сотруднику сама, masterId им не нужен.
 */
const POINT_API = '/api/v2/staff';
const ADMIN_API = '/api/v2/users-staff';

interface MessageResponse {
  success?: boolean;
  message?: string;
}

function messageOf(res: { data?: MessageResponse }, fallback: string): string {
  return (res.data && res.data.message) || fallback;
}

/** Ответ 200 с success: false - отказ сервера, его текст показывается как ошибка */
function ensureSuccess(res: { data?: MessageResponse }, fallback: string): void {
  if (!res.data || !res.data.success) throw new Error(messageOf(res, fallback));
}

const baseOf = (target: StaffTarget): string => (target.mode === 'admin' ? ADMIN_API : POINT_API);

/** Точка админки передаётся явно; у кабинета точка - сама сессия */
function withPoint<T extends object>(target: StaffTarget, data: T): T & { masterId?: number } {
  return target.mode === 'admin' ? { ...data, masterId: target.masterId } : data;
}

export async function fetchStaffOverview(target: StaffTarget): Promise<StaffOverview> {
  const res = await axios.get(baseOf(target), { params: withPoint(target, {}) });
  if (!res.data || !res.data.success || !Array.isArray(res.data.staff) || !Array.isArray(res.data.tree)) {
    throw new Error('Сервер вернул список сотрудников в неожиданном виде');
  }
  return {
    point: res.data.point,
    tree: res.data.tree,
    limit: Number(res.data.limit) || 0,
    active: Number(res.data.active) || 0,
    staff: res.data.staff,
    operator: res.data.operator
  };
}

export async function fetchLoginSuggestions(target: StaffTarget, fullName: string): Promise<LoginSuggestion[]> {
  const res = await axios.get(`${baseOf(target)}/suggestions`, { params: withPoint(target, { fullName }) });
  ensureSuccess(res, 'Не удалось подобрать логин');
  return Array.isArray(res.data.suggestions) ? res.data.suggestions : [];
}

export async function checkStaffLogin(target: StaffTarget, tail: string): Promise<LoginCheck> {
  const res = await axios.get(`${baseOf(target)}/check-login`, { params: withPoint(target, { tail }) });
  ensureSuccess(res, 'Не удалось проверить логин');
  return { available: Boolean(res.data.available), login: res.data.login || null, message: res.data.message || null };
}

/** Пароль не задан - сервер генерирует его и возвращает один раз */
export async function createStaff(target: StaffTarget, input: StaffCreateInput): Promise<StaffCreated> {
  const res = await axios.post(baseOf(target), withPoint(target, { ...input, password: input.password || null }));
  ensureSuccess(res, 'Сервер не подтвердил создание сотрудника');
  return { userId: Number(res.data.userId), login: res.data.login, password: res.data.password || null };
}

export async function saveStaffPermissions(target: StaffTarget, id: number, permissions: string[]): Promise<{ permissions: string[]; message: string }> {
  const res = await axios.put(`${baseOf(target)}/${id}/permissions`, { permissions });
  ensureSuccess(res, 'Сервер не подтвердил сохранение прав');
  return { permissions: Array.isArray(res.data.permissions) ? res.data.permissions : permissions, message: messageOf(res, 'Права сохранены') };
}

/** Пустой пароль - сервер генерирует его и возвращает один раз; сессии сотрудника завершаются */
export async function resetStaffPassword(target: StaffTarget, id: number, newPassword: string): Promise<{ password: string | null; message: string }> {
  const res = await axios.put(`${baseOf(target)}/${id}/password`, { newPassword: newPassword || null });
  ensureSuccess(res, 'Сервер не подтвердил сброс пароля');
  return { password: res.data.password || null, message: messageOf(res, 'Пароль сброшен') };
}

export async function setStaffStatus(target: StaffTarget, id: number, active: boolean): Promise<string> {
  const res = await axios.put(`${baseOf(target)}/${id}/status`, { active });
  ensureSuccess(res, 'Сервер не подтвердил смену статуса');
  return messageOf(res, active ? 'Сотрудник разблокирован' : 'Сотрудник заблокирован');
}

export async function fetchStaffSessions(target: StaffTarget, id: number): Promise<StaffSession[]> {
  const res = await axios.get(`${baseOf(target)}/${id}/sessions`);
  if (!res.data || !res.data.success || !Array.isArray(res.data.sessions)) {
    throw new Error('Сервер вернул список сессий в неожиданном виде');
  }
  return res.data.sessions;
}

export async function revokeStaffSession(target: StaffTarget, id: number, sessionId: number): Promise<string> {
  const res = await axios.delete(`${baseOf(target)}/${id}/sessions/${sessionId}`);
  ensureSuccess(res, 'Сервер не подтвердил завершение сессии');
  return messageOf(res, 'Сессия завершена');
}

export async function revokeAllStaffSessions(target: StaffTarget, id: number): Promise<string> {
  const res = await axios.post(`${baseOf(target)}/${id}/sessions/revoke-all`);
  ensureSuccess(res, 'Сервер не подтвердил завершение сессий');
  return messageOf(res, 'Сессии завершены');
}

/** page - с единицы, как на сервере */
export async function fetchStaffJournal(target: StaffTarget, id: number, page: number, pageSize: number): Promise<StaffJournalPage> {
  const res = await axios.get(`${baseOf(target)}/${id}/audit`, { params: { page, pageSize } });
  if (!res.data || !res.data.success || !Array.isArray(res.data.rows)) {
    throw new Error('Сервер вернул журнал в неожиданном виде');
  }
  return { rows: res.data.rows, total: Number(res.data.total) || 0, page: Number(res.data.page) || page, pageSize: Number(res.data.pageSize) || pageSize };
}

/** Число подчинённых по точкам в зоне оператора - для таблицы «Пользователей» */
export async function fetchStaffCounts(): Promise<Record<number, StaffCount>> {
  const res = await axios.get(`${ADMIN_API}/counts`);
  if (!res.data || !res.data.success || !res.data.counts) throw new Error('Сервер вернул число сотрудников в неожиданном виде');
  return res.data.counts;
}

/** Вход под сотрудником точки (только админка): ответ как у /api/v2/auth/impersonate */
export async function impersonateStaff(id: number): Promise<AuthResponse> {
  const res = await axios.post(`${ADMIN_API}/${id}/impersonate`);
  ensureSuccess(res, 'Не удалось войти под сотрудником');
  return res.data;
}
